import { createState } from 'shared'
import {
	type Collaborator,
	type DApp,
	ErrorType,
	type ProjectDetails,
	type ProjectV2,
	type SmartContract
} from 'core'
import { navigate } from 'wouter-preact/use-browser-location'

import { setSmartContracts } from 'state/smart_contracts'

import { emitSystemNotification } from '../system_notification'
import { application } from '../application'

const projects = createState<ProjectV2[]>([])
const projectDetails = createState<ProjectDetails | null>(null)
const isProjectDetailsLoading = createState(true)
const areProjectsLoading = createState(true)

export const projectsState = projects.state
export const isProjectLoadingState = isProjectDetailsLoading.state
export const projectDetailsState = projectDetails.state
export const areProjectsLoadingState = areProjectsLoading.state

export const setProjects = (projectsToSet: ProjectV2[]) => {
	projects.updateState(() => projectsToSet)
}

export const getProjects = async () => {
	areProjectsLoading.updateState(() => true)
	const { data, error } = await application.value
		.forManagingProjects()
		.getProjectsAndNews()

	if (error) {
		// TODO: Temporal error condition
		emitSystemNotification({
			error: {
				message:
					'An error occurred while fetching projects, please refresh the page',
				type: ErrorType.BadRequest,
				name: 'FetchProjectsError'
			},
			display: 'dialog',
			acceptText: 'Refresh page',
			onCloseCallback: () => window.location.reload()
		})

		return
	}

	// TODO: I think that this logic have to be handle
	// by the adapter / core package instead of the webapp
	const smartContracts = data!.projects
		.flatMap(project =>
			project.smartContracts.map(contract => ({
				projectName: project.name,
				owner: project.owner,
				isCollaborator: project.isCollaborator,
				color: project.color!,
				isWorkspaceReady: false,
				...contract
			}))
		)
		.sort((a, b) => {
			const aDate = new Date(a.createdAt)
			const bDate = new Date(b.createdAt)

			if (aDate > bDate) return -1
			if (aDate < bDate) return 1

			return 0
		})

	setProjects(data!.projects)
	setSmartContracts(smartContracts)
	areProjectsLoading.updateState(() => false)
}

export const updateProject = (project: ProjectV2) => {
	projects.updateState(prevState => {
		const index = prevState.findIndex(p => p.publicId === project.publicId)

		if (index !== -1) {
			prevState[index] = project
			return [...prevState]
		}

		return [project, ...prevState]
	})
}

export const getProjectDetailsById = async (id: string) => {
	isProjectDetailsLoading.updateState(() => true)

	const { data: project, error } = await application.value
		.forManagingProjects()
		.getUserProjectDetails(id)

	isProjectDetailsLoading.updateState(() => false)

	if (error) {
		// TODO: Temporal condition until backend implement `code` property. Then it should be move to the switch statement
		error.message = 'Project not found'

		if (error.code === 'invalid_input') {
			error.message = error.inputErrors!.projectId!
		}

		emitSystemNotification({
			error,
			display: 'dialog',
			onCloseCallback: () => navigate('/', { replace: true })
		})

		return
	}

	// projectId is not set  by the backend, but when creating
	// a dApp is returned. To keep the data consistent, we set it
	// here
	project!.dapps = project!.dapps.map(e => ({
		...e,
		projectId: id
	}))

	projectDetails.updateState(() => project!)
}

export const removeProjectDetail = () => {
	isProjectDetailsLoading.updateState(() => true)
	projectDetails.updateState(() => null)
}

export const addSmartContractToProjectDetail = (
	smartContract: SmartContract
) => {
	if (!projectDetails.state.peek()) return

	projectDetails.updateState(state => {
		state!.dapps = [smartContract, ...state!.dapps]
		return state
	})
}

export const addDAppToProjectDetail = async (projectId: string, dApp: DApp) => {
	if (
		!projectDetails.state.peek() ||
		projectDetails.state.peek()!.publicId !== projectId
	) {
		return
	}

	projectDetails.updateState(state => {
		state!.dapps = [dApp, ...state!.dapps]
		return state
	})
}

// TODO: Have to create the test cases for collaborators
export const addCollaboratorFromProjectDetails = (
	collaborator: Collaborator
) => {
	projectDetails.updateState(state => ({
		...state!,
		collaborators: [collaborator, ...state!.collaborators]
	}))
}

export const removeCollaboratorFromProjectDetails = (
	collaboratorUsername: string
) => {
	projectDetails.updateState(state => ({
		...state!,
		collaborators: state!.collaborators.filter(
			collaborator => collaborator.userHandler !== collaboratorUsername
		)
	}))
}
